import { Inject, Injectable } from '@angular/core';
import { DestinationsProvider } from '@prlw/core/destinations/destinations.provider';
import {
  PRODUCTS_GATEWAY,
  ProductsGateway,
} from '@prlw/core/products/products.gateway';
import { UserSettingsController } from '@prlw/core/user-settings/user-settings.controller';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { ShipmentsState } from '../shipments/shipments.state';
import { ProductGroupsService } from './product-groups.service';
import { Product } from './product.entity';
import { Setting } from '@prlw/core/user-settings/setting.interface';
import {
  IObservableWithPending,
  observableWithPending,
} from '@prlw/infrastructure/observableWithPending';
import { ShipmentsProvider } from '../shipments/shipments.provider';

function extractProductCode(productOrCode: Product | string): string {
  return typeof productOrCode === 'string' ? productOrCode : productOrCode.code;
}

function removeDupes<T>(array: T[]): T[] {
  return [...new Set(array)];
}

const UNSELECTED_PRODUCT_CODES_SETTING = new Setting<string[]>(
  'unselected-products',
);

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly unselectedProductCodesSetting$: Observable<string[]>;

  private readonly selectedShipmentCodes$: Observable<string[]>;
  public readonly productsOfCurrentGroupWithPending$: Observable<
    IObservableWithPending<Product[]>
  >;

  constructor(
    private readonly productGroupsService: ProductGroupsService,
    private readonly userSettings: UserSettingsController,
    @Inject(PRODUCTS_GATEWAY) private readonly gateway: ProductsGateway,
    private readonly destinationsProvider: DestinationsProvider,
    private readonly shipmentsState: ShipmentsState,
    private readonly shipmentsProvider: ShipmentsProvider,
  ) {
    this.unselectedProductCodesSetting$ = userSettings.setting$(
      UNSELECTED_PRODUCT_CODES_SETTING,
      [],
    );
    this.selectedShipmentCodes$ = this.currentProductGroup$.pipe(
      switchMap((group) => shipmentsState.getSelectedShipments(group.code)),
    );

    this.productsOfCurrentGroupWithPending$ = this.selectedShipmentCodes$.pipe(
      switchMap((selectedShipmentCodes) =>
        this.currentProductGroup$.pipe(
          switchMap((currentProductGroup) =>
            observableWithPending(
              this.gateway.getProductsOfGroup(
                currentProductGroup.code,
                selectedShipmentCodes,
              ),
            ),
          ),
        ),
      ),
    );
  }

  currentProductGroup$ = this.productGroupsService.currentProductGroup$;

  allProductsOfCurrentGroup$ = combineLatest([
    this.currentProductGroup$,
    this.shipmentsProvider.shipmentsByCurrentProductGroup$,
  ]).pipe(
    switchMap(([currentProductGroup, shipmentsByCurrentProductGroup]) =>
      this.gateway.getProductsOfGroup(
        currentProductGroup.code,
        shipmentsByCurrentProductGroup.map((shipment) => shipment.code),
      ),
    ),
  );

  public get productsOfCurrentGroup$(): Observable<Product[]> {
    return this.productsOfCurrentGroupWithPending$.pipe(
      filter((item) => 'result' in item),
      map((item) => item.result ?? []),
    );
  }

  public get selectedProductsOfCurrentGroup$(): Observable<Product[]> {
    return combineLatest([
      this.productsOfCurrentGroup$,
      this.unselectedProductCodesSetting$,
    ]).pipe(
      map(([productsOfCurrentGroup, unselectedProducts]) =>
        productsOfCurrentGroup.filter(
          (product) => !unselectedProducts.includes(product.code),
        ),
      ),
    );
  }

  public selectProduct(productOrCode: Product | string): void {
    const code = extractProductCode(productOrCode);
    this.unselectedProductCodesSetting$
      .pipe(
        first(),
        map((unselectedProducts) =>
          unselectedProducts.filter((product) => product !== code),
        ),
        switchMap((newUnselectedProductCodes) =>
          this.userSettings.updateSetting(
            UNSELECTED_PRODUCT_CODES_SETTING,
            newUnselectedProductCodes,
          ),
        ),
      )
      .subscribe();
  }

  public unselectProduct(productOrCode: Product | string): void {
    const code = extractProductCode(productOrCode);
    this.unselectedProductCodesSetting$
      .pipe(
        first(),
        map((unselectedProducts) => removeDupes([...unselectedProducts, code])),
        switchMap((newUnselectedProductCodes) =>
          this.userSettings.updateSetting(
            UNSELECTED_PRODUCT_CODES_SETTING,
            newUnselectedProductCodes,
          ),
        ),
      )
      .subscribe();
  }

  public toggleProduct(productOrCode: Product | string): void {
    const code = extractProductCode(productOrCode);
    this.unselectedProductCodesSetting$
      .pipe(first())
      .subscribe((unselectedProductCodes) => {
        if (unselectedProductCodes.includes(code)) {
          this.selectProduct(code);
        } else {
          this.unselectProduct(code);
        }
      });
  }

  public toggleAllProductsOfCurrentGroup(): void {
    combineLatest([
      this.productsOfCurrentGroup$,
      this.selectedProductsOfCurrentGroup$,
    ])
      .pipe(first())
      .subscribe(([productsOfCurrentGroup, selectedProductsOfCurrentGroup]) => {
        const everyProductSelected =
          productsOfCurrentGroup.length ===
          selectedProductsOfCurrentGroup.length;
        if (everyProductSelected) {
          this.unselectAllProductsOfCurrentGroup();
        } else {
          this.selectAllProductsOfCurrentGroup();
        }
      });
  }

  private selectAllProductsOfCurrentGroup(): void {
    combineLatest([
      this.productsOfCurrentGroup$.pipe(
        map((products) => products.map((product) => product.code)),
      ),
      this.unselectedProductCodesSetting$,
    ])
      .pipe(
        first(),
        switchMap(([productCodesOfCurrentGroup, unselectedProductCodes]) =>
          this.userSettings.updateSetting(
            UNSELECTED_PRODUCT_CODES_SETTING,
            unselectedProductCodes.filter(
              (code) => !productCodesOfCurrentGroup.includes(code),
            ),
          ),
        ),
      )
      .subscribe();
  }

  private unselectAllProductsOfCurrentGroup(): void {
    combineLatest([
      this.productsOfCurrentGroup$.pipe(
        map((products) => products.map((product) => product.code)),
      ),
      this.unselectedProductCodesSetting$,
    ])
      .pipe(
        first(),
        switchMap(([productCodesOfCurrentGroup, unselectedProductCodes]) =>
          this.userSettings.updateSetting(
            UNSELECTED_PRODUCT_CODES_SETTING,
            removeDupes([
              ...unselectedProductCodes,
              ...productCodesOfCurrentGroup,
            ]),
          ),
        ),
      )
      .subscribe();
  }
}
